import styled, { css } from "styled-components";
import type { DynamicCardProps } from "./DynamicCard";

interface CardWrapperProps {
  $type: DynamicCardProps["type"];
  $alignment: "left" | "center";
  $backgroundColor?: string;
  $borderColor?: string;
  $borderRadius?: string;
}

interface CardButtonProps {
  $buttonStyle: "button" | "link";
  $backgroundColor?: string;
  $textColor?: string;
  $borderRadius?: string;
  $borderColor?: string;
  $linkColor?: string;
}

interface CardTagProps {
  $backgroundColor?: string;
  $borderColor?: string;
  $textColor?: string;
}

interface CardContentProps {
  $alignment: "left" | "center";
}

export const CardWrapper = styled.div<CardWrapperProps>`
  display: flex;
  flex-direction: column;
  border-radius: ${({ $borderRadius }) =>
    $borderRadius || "var(--design-card-border-radius, 8px)"};
  overflow: hidden;
  height: 100%;
  border: 1px solid
    ${(props) => props.$borderColor || "var(--cards-border, #e2e8f0)"};
  text-decoration: none;
  cursor: pointer;

  ${({ $type, $backgroundColor }) => {
    switch ($type) {
      case "default":
      case "seminovos":
        return css`
          background-color: ${$backgroundColor ||
          "var(--cards-background, #ffffff)"};
        `;
      case "simple":
        return css`
          background-color: transparent;
          border: none;
        `;
      case "overlaid":
        return css`
          position: relative;
          background-color: transparent;
          border: none;
        `;
      default:
        return "";
    }
  }}

  ${({ $alignment }) =>
    $alignment === "center" &&
    css`
      text-align: center;
    `}
`;

interface CardImageWrapperProps {
  $type: DynamicCardProps["type"];
  $borderRadius?: string;
  $shadowColor?: string;
  $imageAspectRatio?: 'fixed' | 'auto';
  href?: string;
  target?: string;
  rel?: string;
}

export const CardImageWrapper = styled.a<CardImageWrapperProps>`
  display: block;
  text-decoration: none;
  position: relative;

  ${({ $type, $borderRadius, $shadowColor, $imageAspectRatio }) => {
    if ($type === "overlaid") {
      return css`
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;

        &::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 100%;
          background: ${$shadowColor || "#0000001A"};
          z-index: 1;
        }

        .image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      `;
    }
    if ($type === "simple") {
      return css`
        width: 100%;
        ${$imageAspectRatio !== 'auto' ? 'aspect-ratio: 16 / 9;' : ''}
        overflow: hidden;
        border-radius: ${$borderRadius ||
        "var(--design-card-border-radius, 8px)"};

        .image {
          width: 100%;
          ${$imageAspectRatio !== 'auto' ? 'height: 100%;' : 'aspect-ratio: auto;'}
          object-fit: cover;
        }
      `;
    }
    return css`
      width: 100%;
      ${$imageAspectRatio !== 'auto' ? 'aspect-ratio: 16 / 9;' : ''}
      overflow: hidden;

      .image {
        width: 100%;
        ${$imageAspectRatio !== 'auto' ? 'height: 100%;' : 'aspect-ratio: auto;'}
        object-fit: cover;
      }
    `;
  }}
`;

// Badge de cronômetro sobreposto no canto superior da imagem do card.
// z-index acima do overlay (::after, z:1) do card "sobreposto".
export const CardCountdownBadge = styled.div<{ $position?: "left" | "right" }>`
  position: absolute;
  top: 8px;
  z-index: 3;
  pointer-events: none;
  ${({ $position }) => ($position === "right" ? css`right: 8px;` : css`left: 8px;`)}
`;

export const CardContent = styled.div<
  CardContentProps & { $type: DynamicCardProps["type"] }
>`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.75rem;
  padding: 1rem;
  flex: 1;

  ${({ $alignment }) =>
    $alignment === "center" &&
    css`
      justify-items: center;
      text-align: center;
    `}

  ${({ $type }) =>
    $type === "overlaid" &&
    css`
      position: relative;
      z-index: 2;
      min-height: 280px;
      padding: 1.5rem;

      @media (min-width: 768px) {
        min-height: 320px;
      }
    `}
`;

export const CardHeader = styled.div<Pick<CardContentProps, "$alignment">>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  ${({ $alignment }) =>
    $alignment === "center" &&
    css`
      align-items: center;
    `}
`;

export const CardBody = styled.div<Pick<CardContentProps, "$alignment">>`
  display: contents;
  gap: 0.75rem;

  ${({ $alignment }) =>
    $alignment === "center" &&
    css`
      justify-items: center;
      text-align: center;
    `}
`;

export const CardFooter = styled.div<Pick<CardContentProps, "$alignment">>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: auto;

  ${({ $alignment }) =>
    $alignment === "center" &&
    css`
      align-items: center;
    `}
`;

export const CardTag = styled.span<CardTagProps>`
  display: inline-flex;
  min-height: 24px;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 9999px;
  border: 1px solid
    ${({ $borderColor }) => $borderColor || "var(--cards-border, #E2E8F0)"};
  background: ${({ $backgroundColor }) =>
    $backgroundColor || "var(--cards-background, transparent)"};
  width: fit-content;

  color: ${({ $textColor }) => $textColor || "var(--cards-title, #475569)"};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

export const CardTitle = styled.h3<{
  $titleColor?: string;
}>`
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
  color: ${({ $titleColor }) => $titleColor || "var(--cards-title, #0f172a)"};

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const CardDescription = styled.div<{ $descriptionColor?: string; $noClamp?: boolean }>`
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
  color: ${({ $descriptionColor }) =>
    $descriptionColor || "var(--text-body, #475569)"};
  ${({ $noClamp }) =>
    !$noClamp &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const CardSubtitle = styled.div`
  font-size: 1.125rem;
  line-height: 1.4;
  font-weight: 600;
  margin: 0;
  color: var(--text-title, #0f172a);
`;

export const CardDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--cards-border, #e2e8f0);
  margin: 0.25rem 0;
`;

export const CardMetadata = styled.span<{ $metadataColor?: string }>`
  font-size: 0.75rem;
  color: ${({ $metadataColor }) =>
    $metadataColor || "var(--cards-body, #64748b)"};
`;

const cardButtonStyles = css<CardButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.5rem;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: opacity 0.2s ease;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  ${({
    $buttonStyle,
    $backgroundColor,
    $textColor,
    $linkColor,
    $borderRadius,
    $borderColor,
  }) => {
    if ($buttonStyle === "button") {
      return css`
        padding: 0.75rem 1.5rem;
        background-color: ${$backgroundColor ||
        "var(--buttons-background, #3b82f6)"};
        color: ${$textColor || "var(--buttons-text, #ffffff)"};
        width: fit-content;

        border-radius: ${$borderRadius || "var(--design-button-border-radius)"};
        border-width: var(--design-button-border-width, 1px);
        border-style: solid;
        border-color: ${$borderColor || "var(--buttons-border, #3b82f6)"};
      `;
    }
    return css`
      padding: 16px 0px;
      background: transparent;
      color: ${$linkColor || "var(--buttons-background, #3b82f6)"};
      width: fit-content;
      gap: 4px;
    `;
  }}
`;

export const CardButtonVisual = styled.span<CardButtonProps>`
  ${cardButtonStyles}
`;

export const CardButton = styled.a<CardButtonProps>`
  ${cardButtonStyles}
`;

// Specification styles for seminovos card type
export const CardSpecifications = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
`;

export const CardSpecificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

interface CardSpecificationIconProps {
  $iconColor?: string;
}

export const CardSpecificationIcon = styled.div<CardSpecificationIconProps>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: ${({ $iconColor }) => $iconColor || "var(--general-primary, #64748b)"};
  --icon-color: ${({ $iconColor }) =>
    $iconColor || "var(--general-primary, #64748b)"};

  svg {
    width: 16px;
    height: 16px;
  }

  /*
   * Só ícones stroke-based (Lucide/builtin e SVGs legados sem data-icon-library)
   * herdam o traço. Phosphor/Material/FontAwesome e SVGs enviados são fill-based:
   * forçar stroke engrossaria o glifo e contornaria SVGs coloridos.
   */
  svg:not([data-icon-library="phosphor"]):not([data-icon-library="material"]):not([data-icon-library="brands"]):not([data-icon-library="custom"]) {
    stroke: currentColor;
  }
`;

export const CardSpecificationText = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CardSpecificationTitle = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-body, #64748b);
`;

export const CardSpecificationContent = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-title, #0f172a);
`;
